import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ConfigProvider, Typography } from "antd";
import { ArrowRightOutlined, CheckOutlined, CopyOutlined } from "@ant-design/icons";
import { motion, AnimatePresence, useInView, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { useI18n } from "../i18n";
import { useTheme } from "../useTheme";
import { buildTheme } from "../theme";
import { vendorColor } from "../vendorColors";
import { fetchDirectoryCached, type DirectoryItem } from "./directory";
import { VendorAvatar } from "./VendorAvatar";
import { CAP_META, formatContext, formatPrice } from "./modelCatalog";
import { VendorLogo } from "./VendorLogo";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import "./marketing.css";

const { Title, Paragraph, Text } = Typography;

/* ─── i18n ─── */
const t = {
  "zh-CN": {
    hero: {
      h1: "统一接口",
      h1b: "接入所有模型",
      lead: "更优路由，更高可用，一个密钥接入所有供应商。",
      cta1: "获取 API Key",
      cta2: "浏览模型",
    },
    code: {
      copy: "复制",
      copied: "已复制",
    },
    preview: {
      cta: "查看全部模型",
    },
    showcase: {
      routing: {
        title: "智能路由",
        body: "同一模型配置多条通道，请求按权重优先级自动选择最优路径。某条通道故障时，后续请求自动切换到下一候选——用户无感知，线上不中断。",
      },
      tracing: {
        title: "实时追踪",
        body: "每次 API 调用生成唯一请求 ID，记录路由决策、通道选择、上游响应时间。90 天内随时可查——故障排查从分钟缩短到秒级。",
      },
      usage: {
        title: "用量透明",
        body: "实时计量每次请求的 Token 消耗。余额、用量和费用随时可查，按项目和密钥维度汇总——成本从黑盒变成白盒。",
      },
    },
    steps: {
      title: "三步开始",
      items: [
        { num: "1", title: "注册", desc: "凭邀请码注册，获赠试用额度" },
        { num: "2", title: "充值", desc: "按量计费，余额通用" },
        { num: "3", title: "接入", desc: "替换 baseURL，保留原有代码" },
      ],
    },
  },
  en: {
    hero: {
      h1: "The Unified Interface",
      h1b: "For Every Model",
      lead: "Better routing, higher uptime, one API key for all providers.",
      cta1: "Get API Key",
      cta2: "Discover Models",
    },
    code: {
      copy: "Copy",
      copied: "Copied",
    },
    preview: {
      cta: "View all models",
    },
    showcase: {
      routing: {
        title: "Smart Routing",
        body: "Configure multiple channels per model. Requests select the optimal path by weighted priority. When a channel fails, the next request moves to the next candidate — invisible to users, no interruption.",
      },
      tracing: {
        title: "Live Tracing",
        body: "Every API call generates a unique request ID tracking the routing decision, channel selection, and upstream latency. Queryable for 90 days — troubleshooting drops from minutes to seconds.",
      },
      usage: {
        title: "Usage Transparency",
        body: "Every request's token consumption metered in real time. Balance, usage, and cost visible anytime, aggregated by project and key — costs move from black box to glass box.",
      },
    },
    steps: {
      title: "Get started in 3 steps",
      items: [
        { num: "1", title: "Sign up", desc: "Invite code required during beta; trial credit included" },
        { num: "2", title: "Add credits", desc: "Pay as you go, credits work everywhere" },
        { num: "3", title: "Connect", desc: "Swap the baseURL, keep your code" },
      ],
    },
  },
} as const;

/* ─── Quickstart snippets — real endpoints, example domain ─── */
const SNIPPETS = [
  {
    id: "curl",
    label: "cURL",
    code: `curl https://api.lonmaxcloud.com/v1/chat/completions \\
  -H "Authorization: Bearer sk-lonmax-****" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gpt-4o",
    "messages": [{ "role": "user", "content": "Hello!" }]
  }'`,
  },
  {
    id: "python",
    label: "Python",
    code: `from openai import OpenAI

client = OpenAI(
    base_url="https://api.lonmaxcloud.com/v1",
    api_key="sk-lonmax-****",
)

response = client.chat.completions.create(
    model="gpt-4o",
    messages=[{"role": "user", "content": "Hello!"}],
)
print(response.choices[0].message.content)`,
  },
  {
    id: "node",
    label: "Node.js",
    code: `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "https://api.lonmaxcloud.com/v1",
  apiKey: "sk-lonmax-****",
});

const response = await client.chat.completions.create({
  model: "gpt-4o",
  messages: [{ role: "user", content: "Hello!" }],
});
console.log(response.choices[0].message.content);`,
  },
] as const;

/* ─── Tiny display-only snippet tinting ───
   Splits a line into spans: quoted strings, JSON/keyword keys (a quote
   followed by ':' or a word before '='), CLI flags, comments. */
function tintLine(line: string, lineIdx: number): ReactNode {
  if (line === "") return "\u00a0";
  if (/^\s*(#|\/\/)/.test(line)) {
    return <span key={lineIdx} className="mk-c-dim">{line}</span>;
  }
  const parts = line.split(/("(?:[^"\\]|\\.)*")/g).filter((p) => p !== "");
  return (
    <span key={lineIdx}>
      {parts.map((part, i) => {
        const next = parts[i + 1] ?? "";
        if (part.startsWith('"')) {
          const isKey = next.trimStart().startsWith(":");
          return <span key={i} className={isKey ? "mk-c-key" : "mk-c-str"}>{part}</span>;
        }
        const flagMatch = part.match(/^(\s*)(-{1,2}[\w-]+)(.*)$/s);
        const flag = flagMatch?.[2];
        if (flag && flag !== "-d") {
          return (
            <span key={i}>
              {flagMatch?.[1]}
              <span className="mk-c-flag">{flag}</span>
              {tintAssignments(flagMatch?.[3] ?? "", i)}
            </span>
          );
        }
        return <span key={i}>{tintAssignments(part, i)}</span>;
      })}
    </span>
  );
}

/* Words immediately before '=' read as keys in Python/TS snippets. */
function tintAssignments(text: string, key: number): ReactNode {
  const pieces = text.split(/(\w+)(?=\s*=\s*")/g);
  if (pieces.length === 1) return text;
  return (
    <span key={key}>
      {pieces.map((p, i) =>
        i % 2 === 1 ? <span key={i} className="mk-c-key">{p}</span> : p,
      )}
    </span>
  );
}

/* Best-effort clipboard write with a legacy fallback; reports success. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API unavailable (permission/context) — legacy path.
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  try {
    return document.execCommand("copy");
  } finally {
    area.remove();
  }
}

export function CodeWindow({ locale }: { locale: "zh-CN" | "en" }) {
  const [tab, setTab] = useState<(typeof SNIPPETS)[number]["id"]>("curl");
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const active = SNIPPETS.find((s) => s.id === tab) ?? SNIPPETS[0];

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    if (await copyText(active.code)) {
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1600);
    }
  };

  return (
    <div className="mk-code-window">
      <div className="mk-code-window__head">
        <span className="mk-code-window__dots" aria-hidden="true"><span /><span /><span /></span>
        <div className="mk-code-tabs" role="tablist" aria-label="Code language">
          {SNIPPETS.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={tab === s.id}
              className={`mk-code-tab${tab === s.id ? " mk-code-tab--active" : ""}`}
              onClick={() => setTab(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button type="button" className="mk-code-copy" onClick={() => void copy()}>
          {copied ? <CheckOutlined style={{ fontSize: 11, color: "#37c96b" }} /> : <CopyOutlined style={{ fontSize: 11 }} />}
          {copied ? t[locale].code.copied : t[locale].code.copy}
        </button>
      </div>
      <div className="mk-code-window__body">
        <pre aria-label={`${active.label} example`}>
          {active.code.split("\n").map((line, i) => (
            <Fragment key={i}>
              {tintLine(line, i)}
              {"\n"}
            </Fragment>
          ))}
        </pre>
      </div>
    </div>
  );
}

/* ─── Live model preview — real directory data, no mock rows ───
   Ranks by provider count (the honest availability signal a relay
   has, kept internal) and deep-links each row into /models?q=… */
function ModelsPreview({ locale }: { locale: "zh-CN" | "en" }) {
  const [items, setItems] = useState<DirectoryItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchDirectoryCached().then((r) => {
      if (!cancelled && r.state === "ok" && r.items.length > 0) setItems(r.items);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!items) return null;

  /* Flagships first: priced models (richest rows) by context window,
     then the rest by provider count — the honest availability signal. */
  const ranked = [...items].sort((a, b) => {
    const aPriced = a.input_price != null ? 1 : 0;
    const bPriced = b.input_price != null ? 1 : 0;
    if (aPriced !== bPriced) return bPriced - aPriced;
    if (aPriced === 1) return (b.context_tokens ?? 0) - (a.context_tokens ?? 0);
    return b.providers.length - a.providers.length;
  });
  const top = ranked.slice(0, 8);
  const s = t[locale];
  const zh = locale === "zh-CN";

  return (
    <div className="mk-preview">
      <div className="mk-preview__head">
        <h3 className="mk-preview__title">
          {zh ? `已接入 ${items.length} 个模型` : `${items.length} models and counting`}
        </h3>
        <Link to="/models" className="mk-preview__more">
          {s.preview.cta}
          <ArrowRightOutlined style={{ fontSize: 10 }} />
        </Link>
      </div>
      <div className="mk-preview__colhead" aria-hidden="true">
        <span>{zh ? "模型" : "Model"}</span>
        <span>{zh ? "能力" : "Capabilities"}</span>
        <span>{zh ? "上下文" : "Context"}</span>
        <span>{zh ? "输入 /1M" : "Input /1M"}</span>
        <span>{zh ? "输出 /1M" : "Output /1M"}</span>
        <span>{zh ? "厂商" : "Vendor"}</span>
      </div>
      {top.map((m) => (
        <Link key={m.model_name} to="/models" className="mk-preview__row">
          <span className="mk-table-model">
            <VendorAvatar vendor={m.vendor} size={28} />
            <span className="mk-table-model__name">
              <Text strong style={{ fontSize: 13 }}>{m.display_name}</Text>
              <span className="mk-table-model__id">{m.model_name}</span>
            </span>
          </span>
          <span className="mk-preview__caps">
            {m.capabilities.slice(0, 3).map((cap) => {
              const meta = CAP_META[cap.toLowerCase()];
              return (
                <span key={cap} className="mk-cap mk-cap--bare" title={meta ? meta.hint : cap}>
                  {meta ? meta.icon : null}
                  {meta ? (zh ? meta.zh : meta.en) : cap}
                </span>
              );
            })}
          </span>
          <span className={`mk-table-num${m.context_tokens ? "" : " mk-table-num--empty"}`}>
            {formatContext(m.context_tokens)}
          </span>
          <span className={`mk-table-num${m.input_price != null ? "" : " mk-table-num--empty"}`}>
            {formatPrice(m.input_price)}
          </span>
          <span className={`mk-table-num${m.output_price != null ? "" : " mk-table-num--empty"}`}>
            {formatPrice(m.output_price)}
          </span>
          <span className="mk-preview__vendor">
            <span
              className="mk-vendor-dot"
              style={{ background: m.vendor ? vendorColor(m.vendor) : "var(--as-muted)" }}
              aria-hidden="true"
            />
            {m.vendor || (zh ? "未知" : "Unknown")}
          </span>
        </Link>
      ))}
    </div>
  );
}

/* ─── Rotating provider logos ─── */
const PROVIDERS = [
  { name: "OpenAI", color: "#74AA9C" },
  { name: "Anthropic", color: "#D97757" },
  { name: "Google", color: "#4285F4" },
  { name: "DeepSeek", color: "#536AF5" },
  { name: "MiniMax", color: "#FA5210" },
  { name: "Mistral", color: "#FF7000" },
];

function RotatingProviderBadge() {
  const [idx, setIdx] = useState(0);
  // Respect prefers-reduced-motion: skip the rotation entirely.
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => setIdx((n) => (n + 1) % PROVIDERS.length), 2000);
    return () => clearInterval(id);
  }, []);
  const p = PROVIDERS[idx] ?? { name: "OpenAI", color: "#74AA9C" };
  return (
    <span
      aria-hidden="true"
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        borderRadius: "50%",
        overflow: "hidden",
        background: p.color,
      }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={p.name}
          initial={{ opacity: 0, scale: 0.7, rotate: -30 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          exit={{ opacity: 0, scale: 0.7, rotate: 30 }}
          transition={{ duration: 0.3 }}
          style={{ position: "absolute", display: "inline-flex" }}
        >
          {/* White mark on the vendor's brand disc stays legible across
              the rotation; decorative, hence aria-hidden upstream. */}
          <VendorLogo vendor={p.name} size={18} mono="#fff" />
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

/* ─── Connected-vendor marquee — live directory data ───
   Shows the vendors actually reachable through the relay (grouped from
   the public catalog), never a static list of "supported" integrations.
   Two identical groups animate a seamless horizontal loop; hover pauses
   and reduced-motion falls back to a plain scrollable row. */
function ProviderMarquee({ locale }: { locale: "zh-CN" | "en" }) {
  const [vendors, setVendors] = useState<{ name: string; count: number }[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchDirectoryCached().then((r) => {
      if (cancelled || r.state !== "ok") return;
      const counts = new Map<string, number>();
      for (const m of r.items) {
        if (m.vendor === "") continue;
        counts.set(m.vendor, (counts.get(m.vendor) ?? 0) + 1);
      }
      const list = [...counts.entries()]
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
      if (list.length > 0) setVendors(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!vendors) return null;

  return (
    <>
      <div className="mk-container mk-center" style={{ marginBottom: 36 }}>
        <Title level={2} className="mk-h2">
          {locale === "zh-CN" ? `已接入 ${vendors.length} 家厂商` : `${vendors.length} vendors connected`}
        </Title>
        <Paragraph className="mk-sub" style={{ marginTop: 8 }}>
          {locale === "zh-CN" ? "来自实时模型目录，随通道配置即时更新。" : "Live from the model catalog — updates as channels change."}
        </Paragraph>
      </div>
      {/* Full-bleed so the loop travels wider than the content column. */}
      <div className="mk-marquee">
        <div className="mk-marquee__track">
          {[0, 1].map((copy) => (
            <div className="mk-marquee__group" key={copy} aria-hidden={copy === 1}>
              {vendors.map((v) => (
                <div className="mk-marquee__card" key={v.name} role="listitem">
                  <VendorLogo vendor={v.name} size={26} />
                  <span className="mk-marquee__name">{v.name}</span>
                  <span className="mk-marquee__count">
                    {v.count} {locale === "zh-CN" ? "模型" : v.count === 1 ? "model" : "models"}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ─── Hero stats — only numbers the live catalog can prove ───
   Internal architecture counts (providers/protocols/routes) mean
   nothing to a visitor; models, vendors, and context window are what
   a buyer is actually shopping for. */
function HeroStats({ locale }: { locale: "zh-CN" | "en" }) {
  const [stats, setStats] = useState<{ value: number; suffix?: string; label?: string }[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchDirectoryCached().then((r) => {
      if (cancelled || r.state !== "ok" || r.items.length === 0) return;
      const vendors = new Set<string>();
      let maxContext = 0;
      for (const m of r.items) {
        if (m.vendor) vendors.add(m.vendor);
        if (m.context_tokens && m.context_tokens > maxContext) maxContext = m.context_tokens;
      }
      const zh = locale === "zh-CN";
      setStats([
        { value: r.items.length, label: zh ? "在线模型" : "models online" },
        { value: vendors.size, label: zh ? "接入厂商" : "vendors" },
        {
          value: Math.round(maxContext / 1_000_000),
          suffix: zh ? "M 最大上下文" : "M max context",
          label: zh ? "单模型上下文窗口" : "per-model context window",
        },
      ]);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  if (!stats) return <div className="mk-hero-stats" aria-hidden="true" />;
  return (
    <div className="mk-hero-stats">
      {stats.map((item) => (
        <CountUpStat
          key={item.label ?? item.suffix ?? ""}
          value={item.value}
          label={item.label}
          suffix={item.suffix}
        />
      ))}
    </div>
  );
}

/* ─── Count-up stat ─── */
function CountUpStat({ value, label, suffix }: { value: number; label?: string; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const motionVal = useMotionValue(0);
  const spring = useSpring(motionVal, { duration: 1.1, bounce: 0 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (inView) motionVal.set(value);
  }, [inView, value, motionVal]);

  useEffect(() => {
    const unsub = spring.on("change", (v) => setDisplay(Math.round(v)));
    return () => unsub();
  }, [spring]);

  return (
    <div className="mk-hero-stat">
      <span ref={ref} className="mk-hero-stat__value">
        {display}
        {suffix ? <span className="mk-hero-stat__suffix">{suffix}</span> : null}
      </span>
      {label ? <span className="mk-hero-stat__label">{label}</span> : null}
    </div>
  );
}

/* ─── Animation variants ─── */
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const } },
};

/* ─── Feature showcase visuals — live simulations ───
   The panels continuously animate so they read as behaviour, not
   frozen (fake) snapshots. Ticking pauses off-screen, respects
   prefers-reduced-motion, and each panel is labelled as a demo. */

const rnd = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
const hexId = (n: number) => Array.from({ length: n }, () => "0123456789abcdef"[rnd(0, 15)]).join("");

/** Drives a simulation interval only while the panel is on screen and
    motion is allowed. Returns the ref to attach to the panel root. */
function useMockTick(onTick: () => void, intervalMs: number) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-60px" });
  const reduce = useReducedMotion();
  const saved = useRef(onTick);
  useEffect(() => {
    saved.current = onTick;
  });
  useEffect(() => {
    if (reduce || !inView) return;
    const id = setInterval(() => saved.current(), intervalMs);
    return () => clearInterval(id);
  }, [reduce, inView, intervalMs]);
  return ref;
}

function MockupCaption() {
  return <span className="mk-mockup__caption">模拟演示 · simulated</span>;
}

type RouteChannel = { name: string; pct: number; failed: boolean };

function RoutingVisual() {
  const [channels, setChannels] = useState<RouteChannel[]>([
    { name: "Channel A", pct: 62, failed: false },
    { name: "Channel B", pct: 31, failed: false },
    { name: "Channel C", pct: 7, failed: false },
  ]);

  const ref = useMockTick(() => {
    setChannels((prev) => {
      const broken = prev.find((c) => c.failed);
      const recovered = broken && Math.random() < 0.55;
      const failIdx = !broken && Math.random() < 0.22 ? rnd(0, 2) : -1;
      const raw = prev.map((c, i) => {
        if (i === failIdx) return { ...c, pct: 0, failed: true };
        const base = c.failed && !recovered ? Math.max(c.pct, 8) : c.pct;
        const pct = Math.max(4, Math.min(85, base + rnd(-4, 4)));
        return { ...c, pct, failed: false };
      });
      const total = raw.reduce((sum, c) => sum + c.pct, 0);
      return raw.map((c) => ({ ...c, pct: Math.round((c.pct / total) * 100) }));
    });
  }, 1200);

  let top: RouteChannel | undefined;
  for (const c of channels) if (!top || c.pct > top.pct) top = c;

  return (
    <div className="mk-mockup" ref={ref}>
      <div className="mk-mockup__row mk-mockup__row--dim" style={{ marginBottom: 10 }}>
        <span>model:</span>
        <span className="mk-mockup__accent">gpt-4o</span>
      </div>
      {channels.map((c) => (
        <div key={c.name} className="mk-mockup__row" style={{ marginBottom: 8 }}>
          <span style={{ minWidth: 78 }}>{c.name}</span>
          <motion.span
            className={`mk-mockup__bar${top === c && !c.failed ? " mk-mockup__bar--accent" : ""}`}
            animate={{ width: `${Math.max(c.pct * 1.4, 10)}px` }}
            transition={{ type: "spring", stiffness: 140, damping: 22 }}
          />
          <span style={{ minWidth: 32 }}>{c.pct}%</span>
          {c.failed ? (
            <span className="mk-mockup__status--err">✗ 503</span>
          ) : (
            top === c && <span className="mk-mockup__status--ok">✓</span>
          )}
        </div>
      ))}
      <MockupCaption />
    </div>
  );
}

type TraceRow = { key: number; id: string; model: string; chan: string; ms: number; failed: boolean };
const TRACE_MODELS = ["gpt-4o", "claude-3", "gemini", "deepseek"];

function makeTraceRow(): TraceRow {
  return {
    key: Date.now() + Math.random(),
    id: `asrq_${hexId(8)}`,
    model: TRACE_MODELS[rnd(0, TRACE_MODELS.length - 1)] ?? "gpt-4o",
    chan: `Chan-${"ABC"[rnd(0, 2)] ?? "A"}`,
    ms: rnd(70, 420),
    failed: false,
  };
}

function TracingVisual() {
  const [rows, setRows] = useState<TraceRow[]>(() =>
    Array.from({ length: 5 }, () => makeTraceRow()),
  );
  const retryKey = useRef<number | null>(null);

  const ref = useMockTick(() => {
    setRows((prev) => {
      let row: TraceRow;
      const retrying = retryKey.current;
      if (retrying != null) {
        const base = prev.find((r) => r.key === retrying);
        row = { ...makeTraceRow(), id: base?.id ?? makeTraceRow().id, chan: "Chan-A", ms: rnd(70, 300), failed: false };
        retryKey.current = null;
      } else {
        row = makeTraceRow();
        if (Math.random() < 0.2) {
          row.failed = true;
          row.ms = rnd(350, 900);
          retryKey.current = row.key;
        }
      }
      return [row, ...prev.slice(0, 4)];
    });
  }, 1300);

  return (
    <div className="mk-mockup" ref={ref}>
      {/* Fixed-height log window: enter/exit never changes the panel's
          height, so the surrounding showcase block stays perfectly still. */}
      <div className="mk-mockup__log">
        <AnimatePresence initial={false} mode="popLayout">
          {rows.map((row) => (
            <motion.div
              key={row.key}
              layout
              className="mk-mockup__row"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
            >
              <span className="mk-mockup__accent" style={{ minWidth: 108 }}>{row.id}</span>
              <span style={{ minWidth: 62 }}>{row.model}</span>
              <span style={{ minWidth: 56 }}>{row.chan}</span>
              <span style={{ minWidth: 48 }}>{row.ms}ms</span>
              <span className={row.failed ? "mk-mockup__status--err" : "mk-mockup__status--ok"}>
                {row.failed ? "✗ 503 → retry" : "✓ 200"}
              </span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      <MockupCaption />
    </div>
  );
}

const USAGE_NAMES = ["gpt-4o", "claude-3", "gemini-pro"];
function UsageVisual({ locale }: { locale: "zh-CN" | "en" }) {
  const labels = locale === "zh-CN"
    ? { today: "今日", week: "本周", tokens: "tokens" }
    : { today: "Today", week: "This week", tokens: "tokens" };
  const [today, setToday] = useState(12_847_000);
  const [week, setWeek] = useState(89_234_000);
  const [shares, setShares] = useState([67, 28, 5]);

  const ref = useMockTick(() => {
    setToday((t) => t + rnd(4_000, 60_000));
    setWeek((w) => w + rnd(12_000, 140_000));
    setShares((prev) => {
      const drifted = prev.map((p) => Math.max(2, p + rnd(-3, 3)));
      const [a = 0, b = 0, c = 0] = drifted;
      const sum = a + b + c;
      // Pin the total at 100 so the labels always read cleanly.
      const first = Math.round((a / sum) * 100);
      const second = Math.round((b / sum) * 100);
      return [first, second, 100 - first - second];
    });
  }, 1000);

  return (
    <div className="mk-mockup" ref={ref}>
      <div className="mk-mockup__row mk-mockup__row--dim" style={{ justifyContent: "space-between" }}>
        <span>{labels.today}</span>
        <span>{today.toLocaleString("en-US")} {labels.tokens}</span>
      </div>
      <div className="mk-mockup__row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
        <span>{labels.week}</span>
        <span>{week.toLocaleString("en-US")} {labels.tokens}</span>
      </div>
      <div style={{ borderTop: "1px solid rgb(255 255 255 / 10%)", marginBottom: 10 }} />
      {USAGE_NAMES.map((name, i) => {
        const pct = shares[i] ?? 0;
        return (
          <div key={name} className="mk-mockup__row" style={{ marginBottom: 6 }}>
            <span style={{ minWidth: 74 }}>{name}</span>
            <motion.span
              className={`mk-mockup__bar${i === 0 ? " mk-mockup__bar--accent" : ""}`}
              animate={{ width: `${Math.max(pct * 1.6, 8)}px` }}
              transition={{ type: "spring", stiffness: 140, damping: 22 }}
            />
            <span>{pct}%</span>
          </div>
        );
      })}
      <MockupCaption />
    </div>
  );
}

/* ─── Component ─── */
export function LandingPage() {
  const { locale } = useI18n();
  const { resolved } = useTheme();
  const isDark = resolved === "dark";
  const s = t[locale];

  const showcaseFade = (fromLeft: boolean) => ({
    hidden: { opacity: 0, x: fromLeft ? -28 : 28 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const } },
  });

  return (
    <ConfigProvider theme={buildTheme(isDark)}>
      <div className="mk-page">
        <MarketingNav />

        {/* ─── Hero ─── */}
        <section className="mk-hero">
          <motion.div
            className="mk-container mk-hero__inner"
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.div variants={fadeUp}>
              <Title className="mk-h1">
                {s.hero.h1}
                <br />
                <span className="mk-h1__accent">{s.hero.h1b}</span>
              </Title>
            </motion.div>

            <motion.div variants={fadeUp}>
              <Paragraph className="mk-lead">{s.hero.lead}</Paragraph>
            </motion.div>

            <motion.div className="mk-hero__ctas" variants={fadeUp}>
              <Link to="/register" className="mk-btn mk-btn--primary mk-btn--lg">
                {s.hero.cta1}
              </Link>
              <Link to="/models" className="mk-btn mk-btn--secondary mk-btn--lg">
                <RotatingProviderBadge />
                {s.hero.cta2}
                <ArrowRightOutlined style={{ fontSize: 11 }} />
              </Link>
            </motion.div>

            <HeroStats locale={locale} />
          </motion.div>
        </section>

        {/* ─── Live model preview — the catalog is the pitch ─── */}
        <section className="mk-section mk-section--tight mk-section--divided">
          <div className="mk-container">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <ModelsPreview locale={locale} />
            </motion.div>
          </div>
        </section>

        {/* ─── Connected-vendor marquee (live data) ─── */}
        <section className="mk-section mk-section--tight mk-section--divided">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
          >
            <ProviderMarquee locale={locale} />
          </motion.div>
        </section>

        {/* ─── Feature showcase — alternating full-width sections ─── */}
        <section className="mk-section mk-section--divided">
          <div className="mk-container">
            <motion.div
              className="mk-showcase"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
            >
              <motion.div className="mk-showcase__text" variants={showcaseFade(true)}>
                <Title level={3} className="mk-showcase__title">{s.showcase.routing.title}</Title>
                <Paragraph className="mk-showcase__desc">{s.showcase.routing.body}</Paragraph>
              </motion.div>
              <motion.div className="mk-showcase__visual" variants={showcaseFade(false)}>
                <RoutingVisual />
              </motion.div>
            </motion.div>

            <motion.div
              className="mk-showcase mk-showcase--reverse"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
            >
              <motion.div className="mk-showcase__text" variants={showcaseFade(false)}>
                <Title level={3} className="mk-showcase__title">{s.showcase.tracing.title}</Title>
                <Paragraph className="mk-showcase__desc">{s.showcase.tracing.body}</Paragraph>
              </motion.div>
              <motion.div className="mk-showcase__visual" variants={showcaseFade(true)}>
                <TracingVisual />
              </motion.div>
            </motion.div>

            <motion.div
              className="mk-showcase"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
            >
              <motion.div className="mk-showcase__text" variants={showcaseFade(true)}>
                <Title level={3} className="mk-showcase__title">{s.showcase.usage.title}</Title>
                <Paragraph className="mk-showcase__desc">{s.showcase.usage.body}</Paragraph>
              </motion.div>
              <motion.div className="mk-showcase__visual" variants={showcaseFade(false)}>
                <UsageVisual locale={locale} />
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ─── Quickstart — steps beside the code window ─── */}
        <section className="mk-section mk-section--divided">
          <div className="mk-container">
            <motion.div
              className="mk-section-head mk-center"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
            >
              <Title level={2} className="mk-h2">{s.steps.title}</Title>
            </motion.div>
            <motion.div
              className="mk-start"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="mk-start__steps">
                {s.steps.items.map((step) => (
                  <div key={step.title} className="mk-start__step">
                    <span className="mk-quickstart__num">{step.num}</span>
                    <span>
                      <Text strong className="mk-quickstart__title">{step.title}</Text>
                      <Paragraph className="mk-quickstart__desc">{step.desc}</Paragraph>
                    </span>
                  </div>
                ))}
              </div>
              <CodeWindow locale={locale} />
            </motion.div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </ConfigProvider>
  );
}
